import { useEffect, useRef, useState } from "react";
import { getQuote, formatApiError } from "@/lib/api";
import { formatPrice, friendlyName } from "@/lib/symbols";

const DEFAULT_SYMBOLS = ["AAPL", "NVDA", "MSFT", "TSLA", "SPY", "GC=F", "EURUSD=X", "BTC-USD", "ETH-USD"];
const REFRESH_MS = 60_000;

export default function TickerBar({ onSelect }) {
  const [quotes, setQuotes] = useState({});
  const [err, setErr] = useState(null);
  const alive = useRef(true);

  useEffect(() => {
    alive.current = true;
    const load = async () => {
      try {
        const results = await Promise.all(
          DEFAULT_SYMBOLS.map((s) =>
            getQuote(s)
              .then((q) => [s, q])
              .catch(() => [s, null]),
          ),
        );
        if (!alive.current) return;
        const next = {};
        for (const [s, q] of results) next[s] = q;
        setQuotes(next);
        setErr(null);
      } catch (e) {
        if (!alive.current) return;
        setErr(formatApiError(e));
      }
    };
    load();
    const iv = setInterval(load, REFRESH_MS);
    return () => {
      alive.current = false;
      clearInterval(iv);
    };
  }, []);

  const items = DEFAULT_SYMBOLS.map((s) => quotes[s]).filter(Boolean);
  const rendered = items.length ? items.concat(items) : [];

  return (
    <div
      data-testid="ticker-bar"
      className="flex items-center gap-3 overflow-hidden border border-[var(--aurion-border)] bg-[var(--aurion-panel-2)] px-3 py-2"
    >
      <div className="flex shrink-0 items-center gap-2 border-r border-[var(--aurion-border)] pr-3">
        <span className="aurion-pulse-dot" />
        <span className="font-heading text-[10px] font-semibold tracking-[0.28em] text-[var(--aurion-gold)]">
          LIVE
        </span>
      </div>
      {err ? (
        <span className="text-xs text-[var(--aurion-red)]" data-testid="ticker-err">
          {err}
        </span>
      ) : items.length === 0 ? (
        <span className="text-xs text-[var(--aurion-muted)]">Loading quotes…</span>
      ) : (
        <div className="relative flex-1 overflow-hidden">
          <div className="aurion-ticker-track">
            {rendered.map((q, i) => (
              <button
                key={`${q.symbol}-${i}`}
                onClick={() => onSelect?.(q.symbol)}
                data-testid={`ticker-item-${q.symbol}`}
                title={friendlyName(q.symbol, q.name)}
                className="mx-4 inline-flex items-baseline gap-2 text-xs hover:text-[var(--aurion-gold)] focus:outline-none"
              >
                <span className="font-heading font-semibold tracking-widest text-[var(--aurion-text)]">
                  {friendlyName(q.symbol, q.symbol)}
                </span>
                <span className="font-mono text-[var(--aurion-muted-strong)]">
                  {typeof q.price === "number" ? formatPrice(q.symbol, q.price) : q.price}
                </span>
                <span
                  className={`font-mono ${
                    (q.change_pct || 0) >= 0
                      ? "text-[var(--aurion-green)]"
                      : "text-[var(--aurion-red)]"
                  }`}
                >
                  {(q.change_pct || 0) >= 0 ? "+" : ""}
                  {Number(q.change_pct || 0).toFixed(2)}%
                </span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
